// useMedia — the band-scoped media upload + view machinery the release Assets section rides,
// and the first FE consumer of the media flow. Two pieces:
//   • useUploadMedia → the three-step flow: POST /media/upload-url (mint + 422 too-large/unsupported
//     BEFORE any bytes move) → PUT the bytes to the returned URL (XHR, so we get real upload progress fetch
//     can't report) → POST /media/{id}/complete (registers media.media_assets FROM the actual blob). A failure
//     at ANY step throws — the caller surfaces it and NEVER reports a false success. Go-live: the mint
//     returns an absolute S3 presigned URL (PUT direct, no bearer). Degrade: a same-origin `/blob` path (PUT
//     with the bearer). We branch on whether the URL is same-origin-relative.
//   • useMediaObjectUrl(mediaId, serverUrl) → resolve a media id to a renderable/playable URL. Go-live the
//     entity payload already carries a CDN `url` (use it verbatim). Degrade the payload url is null, so we
//     fetch the authenticated `/raw` bytes and hand back an object URL. A raw fetch (not the typed client) so
//     a media 404/401 never trips the global clear-on-401 logout — a missing cover is not an auth event. The
//     bytes resolve through a SHARED cache (below), so identical (band, media, variant) triples share one
//     request and survive an unmount.
import { useEffect, useState } from "react";
import type { components } from "@cntrl/api-client";
import { API_URL } from "../../config/env.js";
import { useApp } from "../appContext.js";
import { useBandContext, useBandContextOrNull } from "../bandContext.js";
import { bandScopedPath } from "../orgLens.js";
import { registerStoreReset } from "../stores.js";
import { ApiError } from "./apiError.js";

type MediaAssetDto = components["schemas"]["MediaAssetDto"];

export type { MediaAssetDto };

/** The media.media_assets `kind` vocabulary — distinct from the release-asset kind.*/
export type MediaKind = "image" | "audio" | "video" | "pdf" | "document" | "other";

/**
 * True iff the URL is a SAME-ORIGIN relative path (a single leading "/", not a `//host` protocol-relative URL).
 * The bearer + credentials attach ONLY to same-origin degrade PUTs — a protocol-relative `//host` resolves to a
 * cross-origin absolute URL under the one-host `API_URL===""` default, so a naive `startsWith("/")` would leak
 * the bearer to a third-party host. An absolute presigned S3 URL (go-live) fails this and is PUT bearer-free.
 */
function isSameOriginRelative(url: string): boolean {
  return url.startsWith("/") && !url.startsWith("//");
}

/** Derive the media kind from a File's MIME type (defaults to `other` for anything unrecognised). */
export function mediaKindForFile(file: File): MediaKind {
  const t = file.type;
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("audio/")) return "audio";
  if (t.startsWith("video/")) return "video";
  if (t === "application/pdf") return "pdf";
  return "other";
}

export interface UploadOptions {
  /** The media kind (image/audio/video/…) — derive from the file with {@link mediaKindForFile}. */
  kind: MediaKind;
  /** A free-text purpose label stored on the row (e.g. "release-cover", "master", "release-photo"). */
  purpose?: string;
  /** Progress callback in [0,1]; fired from the XHR upload's `progress` events (best-effort). */
  onProgress?: (fraction: number) => void;
  /** Aborts the byte transfer if the caller cancels (the editor's Cancel). */
  signal?: AbortSignal;
}

// A file at or below this size takes the single PUT; a larger one (a release master or an Atmos/variant stem —
// hundreds of MB, ART-218) uploads in parts via the multipart routes. Kept safely under the blob route's 64 MiB
// request cap so a single PUT can never be refused for size. Parts run a few at a time.
const MULTIPART_THRESHOLD = 48 * 1024 * 1024;
const PART_CONCURRENCY = 3;

/**
 * The upload flow, bound to the acting band. Returns `uploadMedia(file, opts)` → the registered
 * MediaAssetDto (its `mediaId` is what the caller stores on the release asset/track/cover). Throws on a
 * rejected mint (422), a failed byte PUT, or an incomplete finalize (422 upload-incomplete) — the upload never
 * reports success without persistence. Nothing here caches; the caller owns the pending/progress UI.
 */
export function useUploadMedia(bandIdOverride?: string | null) {
  const { api, tokenProvider } = useApp();
  // The ambient band, EXCEPT where the caller names one. A surface with no BandProvider mounted — the inbox
  // under the roster shell — has no ambient band to bind to, and the throwing context took the whole dialog
  // down there (the CrewProfileModal rule). A show chat knows its band (`show.bandId`, the same id its flyer
  // already serves from), so it passes one. Nothing else changed: an omitted override resolves the ambient
  // band exactly as before, and having NEITHER is still a throw rather than a silent no-band upload.
  const ambient = useBandContextOrNull();
  const resolved = bandIdOverride ?? ambient?.bandId;
  if (!resolved) throw new Error("useUploadMedia needs a band — mount a BandProvider or pass one.");
  const bandId: string = resolved;

  async function uploadMedia(file: File, opts: UploadOptions): Promise<MediaAssetDto> {
    const contentType = file.type || "application/octet-stream";

    // 1) Reserve — validates size/type at the mint (422 file-too-large / unsupported-media before bytes move).
    const mint = await api.POST("/api/v1/bands/{bandId}/media/upload-url", {
      params: { path: { bandId } },
      body: {
        kind: opts.kind,
        contentType,
        sizeBytes: file.size,
        purpose: opts.purpose ?? null,
        originalFilename: file.name || null,
      },
    });
    if (!mint.response.ok || !mint.data?.mediaId || !mint.data?.uploadUrl) {
      throw new ApiError(mint.response.status, mint.error);
    }
    const { mediaId, uploadUrl, method } = mint.data;

    // Large files upload in parts (ART-218) — the single-PUT blob route is request-size capped, so a master or
    // Atmos stem over the threshold goes through the multipart routes instead. The mint above already validated
    // the declared size against the per-kind cap and gave us the media id.
    if (file.size > MULTIPART_THRESHOLD) {
      return await uploadMultipart({
        bandId,
        mediaId,
        file,
        contentType,
        opts,
        getBearer: () => tokenProvider.getBearer(),
      });
    }

    // 2) PUT the bytes. Same-origin degrade → prefix the API origin + carry the bearer; absolute presigned S3
    //    URL → PUT direct (its auth is in the query). XHR (not fetch) so `upload.onprogress` can drive the bar.
    await putBytes({
      url: uploadUrl,
      method: method || "PUT",
      file,
      contentType,
      bearer: isSameOriginRelative(uploadUrl) ? tokenProvider.getBearer() : null,
      onProgress: opts.onProgress,
      signal: opts.signal,
    });

    // 3) Finalize — the server STATS the actual blob and refuses (422 upload-incomplete) if it never landed.
    const done = await api.POST("/api/v1/bands/{bandId}/media/{mediaId}/complete", {
      params: { path: { bandId, mediaId } },
      body: {
        kind: opts.kind,
        contentType,
        purpose: opts.purpose ?? null,
        originalFilename: file.name || null,
      },
    });
    if (!done.response.ok || !done.data) throw new ApiError(done.response.status, done.error);
    return done.data;
  }

  return { uploadMedia };
}

interface PutBytesArgs {
  url: string;
  method: string;
  file: File;
  contentType: string;
  bearer: string | null;
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/** XHR byte PUT with progress. Resolves on 2xx; rejects (ApiError) otherwise so the flow stops before complete. */
function putBytes(args: PutBytesArgs): Promise<void> {
  const target = isSameOriginRelative(args.url) ? `${API_URL}${args.url}` : args.url;
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(args.method, target);
    if (args.bearer) {
      xhr.setRequestHeader("Authorization", `Bearer ${args.bearer}`);
      xhr.withCredentials = true; // same-origin degrade: send the reissue cookie alongside the bearer
    }
    xhr.setRequestHeader("Content-Type", args.contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && args.onProgress) args.onProgress(e.total > 0 ? e.loaded / e.total : 0);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new ApiError(xhr.status, { title: "Upload failed", detail: "The file couldn't be uploaded." }));
    xhr.onerror = () => reject(new ApiError(0, { title: "Upload failed", detail: "The upload connection failed." }));
    xhr.onabort = () => reject(new ApiError(0, { title: "Upload cancelled", detail: "The upload was cancelled." }));
    if (args.signal) {
      if (args.signal.aborted) {
        xhr.abort();
        return;
      }
      args.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(args.file);
  });
}

// ── Multipart upload (large media: release masters + Atmos/variant stems, ART-218) ──────────────────────────
//
// begin → PUT each part (a few at a time, XHR for progress) → complete (or abort on failure/cancel). The server
// holds no per-upload state: we carry the uploadId and the collected part etags. These are RAW fetch/XHR (like
// putBytes + the /raw view above), so they resolve `bandScopedPath` themselves — a bare fetch never meets the
// org-lens middleware, so under the roster lens a band-direct route would 404 for an org seat.

interface MultipartArgs {
  bandId: string;
  mediaId: string;
  file: File;
  contentType: string;
  opts: UploadOptions;
  getBearer: () => string | null;
}

async function uploadMultipart(a: MultipartArgs): Promise<MediaAssetDto> {
  const { bandId, mediaId, file, contentType, opts, getBearer } = a;

  const post = (suffix: string, body: unknown): Promise<Response> => {
    const bearer = getBearer();
    return fetch(`${API_URL}${bandScopedPath(bandId, suffix)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}) },
      credentials: "include",
      body: JSON.stringify(body),
    });
  };

  // 1) Begin — the server opens the multipart upload and tells us the part size to slice to.
  const beginRes = await post(`media/${mediaId}/multipart`, { contentType });
  if (!beginRes.ok) throw new ApiError(beginRes.status, await safeProblem(beginRes));
  const { uploadId, partSize } = (await beginRes.json()) as { uploadId: string; partSize: number };

  const totalParts = Math.max(1, Math.ceil(file.size / partSize));
  const loaded = new Array<number>(totalParts).fill(0);
  const reportProgress = () => {
    if (!opts.onProgress) return;
    const sum = loaded.reduce((s, n) => s + n, 0);
    opts.onProgress(file.size > 0 ? Math.min(1, sum / file.size) : 1);
  };

  // One controller aborts every in-flight part on the caller's cancel OR on the first part failure.
  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener("abort", onExternalAbort, { once: true });
  }

  const parts: { partNumber: number; etag: string }[] = [];
  try {
    // 2) Upload parts with bounded concurrency. Parts may finish out of order; the server orders by part number.
    let nextIndex = 0;
    const worker = async () => {
      for (;;) {
        const i = nextIndex++;
        if (i >= totalParts) return;
        const partNumber = i + 1;
        const start = i * partSize;
        const slice = file.slice(start, Math.min(start + partSize, file.size));
        const suffix = `media/${mediaId}/multipart/part?uploadId=${encodeURIComponent(uploadId)}&partNumber=${partNumber}`;
        const etag = await putPart({
          url: `${API_URL}${bandScopedPath(bandId, suffix)}`,
          blob: slice,
          contentType,
          bearer: getBearer(),
          signal: controller.signal,
          onProgress: (n) => {
            loaded[i] = n;
            reportProgress();
          },
        });
        parts.push({ partNumber, etag });
      }
    };
    await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, totalParts) }, () => worker()));

    // 3) Complete — assemble + register, exactly like a single-PUT complete (422 upload-incomplete if absent).
    const completeRes = await post(`media/${mediaId}/multipart/complete`, {
      uploadId,
      parts,
      kind: opts.kind,
      contentType,
      purpose: opts.purpose ?? null,
      originalFilename: file.name || null,
    });
    if (!completeRes.ok) throw new ApiError(completeRes.status, await safeProblem(completeRes));
    return (await completeRes.json()) as MediaAssetDto;
  } catch (err) {
    // Best-effort abort so the store doesn't keep (and bill for) the orphaned parts; the lifecycle rule is the
    // backstop if this never lands.
    void post(`media/${mediaId}/multipart/abort`, { uploadId }).catch(() => {});
    throw err;
  } finally {
    if (opts.signal) opts.signal.removeEventListener("abort", onExternalAbort);
  }
}

interface PutPartArgs {
  url: string;
  blob: Blob;
  contentType: string;
  bearer: string | null;
  signal: AbortSignal;
  onProgress: (loadedBytes: number) => void;
}

/** XHR PUT of one part; resolves to its etag (from the JSON response) or rejects so the flow aborts. */
function putPart(args: PutPartArgs): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", args.url);
    if (args.bearer) {
      xhr.setRequestHeader("Authorization", `Bearer ${args.bearer}`);
      xhr.withCredentials = true;
    }
    xhr.setRequestHeader("Content-Type", args.contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) args.onProgress(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText) as { etag: string };
          args.onProgress(args.blob.size); // pin this part to 100% for the aggregate
          resolve(body.etag);
        } catch {
          reject(new ApiError(xhr.status, { title: "Upload failed", detail: "A file part returned a malformed response." }));
        }
      } else {
        reject(new ApiError(xhr.status, { title: "Upload failed", detail: "A file part couldn't be uploaded." }));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, { title: "Upload failed", detail: "The upload connection failed." }));
    xhr.onabort = () => reject(new ApiError(0, { title: "Upload cancelled", detail: "The upload was cancelled." }));
    if (args.signal.aborted) {
      xhr.abort();
      return;
    }
    args.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(args.blob);
  });
}

/** Best-effort parse of a problem-details body for an ApiError (never throws). */
async function safeProblem(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return { title: "Upload failed", detail: `The upload failed (HTTP ${res.status}).` };
  }
}

/**
 * A LAZY (click-triggered) media download — never eager, so a 50 MB master isn't fetched just to list it. Returns
 * `download(mediaId, filename?, serverUrl?)`: uses the CDN `serverUrl` verbatim when present (go-live), else
 * fetches the authenticated `/raw` bytes and triggers a browser download via a transient object URL. A raw fetch
 * so a media 404/401 never trips the global clear-on-401 logout. Rejects on failure (the caller surfaces it).
 */
export function useMediaDownload() {
  const { tokenProvider } = useApp();
  const { bandId } = useBandContext();

  async function download(mediaId: string, filename?: string | null, serverUrl?: string | null): Promise<void> {
    let objectUrl: string | null = null;
    try {
      let href = serverUrl ?? undefined;
      if (!href) {
        const bearer = tokenProvider.getBearer();
        // A download is ALWAYS the original file — never a rendition.
        // bandScopedPath, not a literal: a bare fetch never meets orgLensMiddleware, so under the roster
        // lens this would ask for a band-direct route an org seat cannot resolve. See the helper.
        const res = await fetch(`${API_URL}${bandScopedPath(bandId, `media/${mediaId}/raw`)}`, {
          headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
          credentials: "include",
        });
        if (!res.ok) throw new ApiError(res.status, { title: "Download failed", detail: "The file couldn't be fetched." });
        objectUrl = URL.createObjectURL(await res.blob());
        href = objectUrl;
      }
      const a = document.createElement("a");
      a.href = href;
      a.download = filename || mediaId;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  }

  return { download };
}

export interface MediaObjectUrl {
  /** The resolved URL (server CDN url, or an object URL for the degrade `/raw` bytes), or undefined. */
  url?: string;
  loading: boolean;
  /** True iff resolving the bytes failed (the caller shows its designed fallback, never a broken image). */
  error: boolean;
}

// ---- the shared blob-resolver cache --------------------------------------------------------------------
//
// One entry per (bandId, mediaId, variant). It exists because a LIST surface renders the same cover several
// times: the releases hub paints one release's art in the spotlight, the catalog card, the smart-link chip
// and the prep chip, and each hook instance used to own its own `fetch` + `URL.createObjectURL` + revoke.
// Measured on the hub before this cache: **17 authenticated media requests on mount** for 8 releases —
// including the SAME id fetched twice concurrently at the same `thumb` size — and **8 more full re-downloads**
// after one filter round-trip, because the cards unmount and each object URL is revoked. The variant endpoint
// is `no-store` until the rendition exists, so the HTTP cache cannot absorb any of it.
//
// Consequences, deliberately:
//  • The in-flight promise is SHARED, so there is no per-caller `AbortController` — a caller that unmounts
//    stops listening but the download completes and lands in the cache, which is exactly what makes the
//    filter round-trip free.
//  • Object URLs stay alive for the session, bounded by an LRU of MEDIA_CACHE_MAX; an evicted entry is
//    revoked, so the tab cannot accumulate blobs without limit.
//  • A FAILED resolve is never cached — the entry is dropped, so a remount retries.
//  • Keyed by bandId, and purged by the switchBand/logout scorch (`registerStoreReset`), so one tenant's
//    bytes can never be served into another's session.
const MEDIA_CACHE_MAX = 64;

interface MediaCacheEntry {
  promise: Promise<string>;
  /** Set once resolved — the object URL to revoke on eviction. */
  url?: string;
}

const mediaCache = new Map<string, MediaCacheEntry>();

function resolveMediaUrl(key: string, fetcher: () => Promise<string>): Promise<string> {
  const hit = mediaCache.get(key);
  if (hit) {
    mediaCache.delete(key); // LRU touch: re-insert at the tail
    mediaCache.set(key, hit);
    return hit.promise;
  }
  const entry: MediaCacheEntry = { promise: fetcher() };
  entry.promise.then(
    (url) => {
      entry.url = url; // remembered so eviction can revoke it
    },
    () => {
      if (mediaCache.get(key) === entry) mediaCache.delete(key); // a failure is never cached
    },
  );
  mediaCache.set(key, entry);
  while (mediaCache.size > MEDIA_CACHE_MAX) {
    const oldest = mediaCache.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    const evicted = mediaCache.get(oldest);
    mediaCache.delete(oldest);
    if (evicted?.url) URL.revokeObjectURL(evicted.url);
  }
  return entry.promise;
}

/** Drop every cached blob (revoking its object URL). Wired into the switchBand/logout scorch. */
export function clearMediaCache(): void {
  for (const entry of mediaCache.values()) if (entry.url) URL.revokeObjectURL(entry.url);
  mediaCache.clear();
}

registerStoreReset(clearMediaCache);

/**
 * Resolve a media id to a renderable URL. `serverUrl` (the entity payload's CDN url) wins verbatim when present
 * (go-live). Otherwise, if a `mediaId` is given, fetch the authenticated `/raw` bytes and return an object URL
 * from the shared cache above. A raw fetch (not the typed client) so a media 404/401 does not trip the global
 * clear-on-401 logout. No mediaId and no url → `{ loading:false }` (the caller falls back to its placeholder).
 */
export function useMediaObjectUrl(
  mediaId: string | null | undefined,
  serverUrl?: string | null,
  opts?: {
    /**
     * Fetch from a band OTHER than the active one. The band switcher needs it: its dropdown lists every
     * seat, and each band's photo lives behind that band's own `/bands/{bandId}/media/…` route (the tenant
     * middleware reach-checks it against the caller's seats, so a seat you hold resolves and one you do
     * not 404s). Omit it and the active band is used, which is what every in-page caller wants.
     */
    bandId?: string | null;
    /**
     * Ask for a RENDITION instead of the original: `thumb` (≤320px) for anything icon-sized, `sm` (≤720px)
     * for a preview. Omit it for a full-size render.
     *
     * The endpoint stands the original in until the worker generates the rendition, and marks that response
     * `no-store`, so an icon shown seconds after upload still renders and quietly swaps to the real
     * thumbnail on the next load. Passing `thumb` is never wrong — it degrades, it does not fail.
     */
    variant?: "thumb" | "sm";
  },
): MediaObjectUrl {
  const { tokenProvider } = useApp();
  // OrNull, not the throwing variant: the band switcher renders in the SHELL, above <BandProvider>, and
  // supplies its own band id per row. A caller that supplies neither simply resolves nothing.
  const activeBand = useBandContextOrNull();
  const bandId = opts?.bandId ?? activeBand?.bandId;
  const variant = opts?.variant;
  const [state, setState] = useState<MediaObjectUrl>({ loading: Boolean(mediaId && !serverUrl), error: false });

  useEffect(() => {
    if (serverUrl) {
      setState({ url: serverUrl, loading: false, error: false });
      return;
    }
    // No band context = no route to fetch from; resolving nothing beats issuing `/bands/undefined/media/…`.
    if (!mediaId || !bandId) {
      setState({ loading: false, error: false });
      return;
    }
    let live = true;
    setState({ loading: true, error: false });
    const path = variant ? `media/${mediaId}/variant/${variant}` : `media/${mediaId}/raw`;
    resolveMediaUrl(`${bandId}|${mediaId}|${variant ?? "raw"}`, async () => {
      const bearer = tokenProvider.getBearer();
      const res = await fetch(`${API_URL}${bandScopedPath(bandId, path)}`, {
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
        credentials: "include",
      });
      if (!res.ok) throw new Error(`media ${res.status}`);
      return URL.createObjectURL(await res.blob());
    }).then(
      (url) => {
        // No revoke on teardown: the URL belongs to the shared cache now, and a sibling slot (or this one
        // after a filter toggle) reads the same one. Eviction owns the revoke.
        if (live) setState({ url, loading: false, error: false });
      },
      () => {
        if (live) setState({ loading: false, error: true });
      },
    );
    return () => {
      live = false;
    };
  }, [mediaId, serverUrl, bandId, variant, tokenProvider]);

  return state;
}

/**
 * Resolve a SHARED show's flyer to a renderable URL (ART-165). A guest band holds the host's `flyerMediaId`
 * but the bytes live in the HOST's tenant, so the ordinary band-scoped `/media/…` route 404s them — the RLS
 * fence is the guest's own band. This fetches the guest-reachable
 * `/bands/{bandId}/shared-shows/{showId}/flyer/variant/{variant}` route instead, which serves the bytes
 * cross-tenant after the SAME authorization the shared-show matrix takes (an accepted/invited share + an
 * active friendship). Same shared-cache + object-URL machinery as {@link useMediaObjectUrl}, so a preview and
 * a booking row asking for the same flyer share one request.
 *
 * `hasFlyer` gates the fetch: the caller passes `detail.flyerMediaId != null`, so a flyer-less show resolves
 * nothing instead of issuing a guaranteed 404.
 */
export function useSharedFlyerUrl(
  showId: string | null | undefined,
  hasFlyer: boolean,
  variant: "thumb" | "sm" | "poster" = "sm",
): MediaObjectUrl {
  const { tokenProvider } = useApp();
  // The guest reads a shared show in their OWN band context (never the roster lens), so the active band is the
  // right — and only — tenant to route through; bandScopedPath's org-lens rewrite is a no-op here.
  const activeBand = useBandContextOrNull();
  const bandId = activeBand?.bandId;
  const [state, setState] = useState<MediaObjectUrl>({
    loading: Boolean(showId && hasFlyer && bandId),
    error: false,
  });

  useEffect(() => {
    if (!showId || !hasFlyer || !bandId) {
      setState({ loading: false, error: false });
      return;
    }
    let live = true;
    setState({ loading: true, error: false });
    const suffix = `shared-shows/${showId}/flyer/variant/${variant}`;
    resolveMediaUrl(`shared-flyer|${bandId}|${showId}|${variant}`, async () => {
      const bearer = tokenProvider.getBearer();
      const res = await fetch(`${API_URL}${bandScopedPath(bandId, suffix)}`, {
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
        credentials: "include",
      });
      if (!res.ok) throw new Error(`shared flyer ${res.status}`);
      return URL.createObjectURL(await res.blob());
    }).then(
      (url) => {
        if (live) setState({ url, loading: false, error: false });
      },
      () => {
        if (live) setState({ loading: false, error: true });
      },
    );
    return () => {
      live = false;
    };
  }, [showId, hasFlyer, bandId, variant, tokenProvider]);

  return state;
}
